import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useParams, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { axios } from '../../context/auth'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync'
import _find from 'lodash.find'

import FormError, { FormInfo } from '../../styled/form-error'
import InputGroup, { SmallInputGroup } from '../../styled/input-group'
import { PrimaryButton, SmallButton } from '../../styled/button'
import Select from '../../styled/select'

import SongContent from './content'
import ContextMenu from './context-menu'
import AutoChord from './auto-chord'
import EditMonospace from './edit-monospace'
import formatFromPaste, { looksLikeChordsAbovePaste } from './auto-paste'
import confirm from '../confirmable'
import VersionManager from './version-manager'
// import HelpContent from './help'
import DetectKey from './key'
import transposeChord from '../../utils/transpose-chord'

import { pageView, PAGES } from '../../utils/analytics-tracker'

import { SONG_KEYS, SIGNATURES } from '../../constants'

const TextArea = styled.textarea`
  ${({ monospace }) => monospace && 'font-family: "Lucida Console", Courier, monospace;'}
`

const escape = (str) => {
  try {
    return str.replace(/\\n/g, '\\n')
      .replace(/\\'/g, "\\'")
      .replace(/\\"/g, '\\"')
      .replace(/\\&/g, '\\&')
      .replace(/\\r/g, '\\r')
      .replace(/\\t/g, '\\t')
      .replace(/\\b/g, '\\b')
      .replace(/\\f/g, '\\f')
  } catch (e) {
    console.log('error in song edit escape: ', e)
    return str
  }
}

const urlSpaceReplace = (str) => {
  try {
    return str.replace(/ /g, '+')
  } catch (e) {
    console.log('error in urlSpaceReplace', e)
    return str
  }
}

const getYoutubeSearchUrl = (artist, title) => {
  const searchArtist = urlSpaceReplace(artist)
  const searchTitle = urlSpaceReplace(title)
  return `https://www.youtube.com/results?search_query=${searchArtist}+${searchTitle}`
}

const PreviewContainer = styled.div`
  width: 100%;
  margin: auto;
`

const PreviewLeft = styled.div`
  width: 50%;
  float: left;
  @media (max-width: 1024px) {
    width: 100%;
  }
`

const PreviewRight = styled.div`
  margin-left: 50%;
  padding-left: 15px;
  @media (max-width: 1024px) {
    display: none;
  }
`

const TransposeButtonGroup = styled.div`
  display: inline-flex;
  gap: 8px;
  margin-left: 15px;
  align-items: center;
`

const EditSongSchema = Yup.object().shape({
  artist: Yup.string().min(2).max(150).required().label('Song Artist'),
  title: Yup.string().min(2).max(150).required().label('Song Title'),
  content: Yup.string().max(10000).required().label('Song Content'),
  media: Yup.string().max(300).label('Media Link').optional(),
  key: Yup.string().max(16).optional(),
  tempo: Yup.string().max(16).optional(),
  duration: Yup.string().max(16).optional(),
  signature: Yup.string().max(2).optional(),
  meta1: Yup.string().max(64).optional(),
  meta2: Yup.string().max(64).optional(),
  meta3: Yup.string().max(64).optional(),
  meta4: Yup.string().max(64).optional(),
  notes: Yup.string().max(3000).label('Song Notes').optional()
})

const EditSong = () => {
  const location = useLocation()
  const { songid } = useParams()
  const navigate = useNavigate()
  const isEdit = location.pathname.endsWith('edit')
  const [editInMonospace, setEditInMonospace] = useState(false)
  const [contextEvent, setContextEvent] = useState()
  const [versions, setVersions] = useState([])
  const [activeVersionId, setActiveVersionId] = useState(null)
  // const [, setContext] = useState()

  const { errors, values, handleBlur, handleChange, setValues, handleSubmit, setFieldValue, setErrors, touched } = useFormik({
    initialValues: {
      title: '',
      artist: '',
      content: '',
      media: '',
      key: '',
      duration: '',
      tempo: '',
      transposed: 0,
      signature: '',
      meta1: '',
      meta2: '',
      meta3: '',
      meta4: '',
      _id: undefined
    },
    validationSchema: EditSongSchema,
    onSubmit: (formValues) => save(formValues)
  })

  const save = useCallback(async (values) => {
    // TODO - validate!
    const url = isEdit ? `/api/songs/${values._id}` : '/api/songs'
    const method = isEdit ? 'PUT' : 'POST'

    try {
      // When editing, save to the specific version first
      if (isEdit && activeVersionId) {
        // Update the specific version content
        await axios.put(`/api/songs/${values._id}/versions/${activeVersionId}`, {
          content: values.content
        })
      }

      // Prepare data for song update (metadata only when editing)
      const dataToSave = {
        title: escape(values.title),
        artist: values.artist,
        media: values.media,
        key: values.key,
        duration: values.duration,
        tempo: values.tempo,
        meta1: values.meta1,
        meta2: values.meta2,
        meta3: values.meta3,
        meta4: values.meta4,
        transposed: isEdit ? values.transposed : 0,
        signature: values.signature
      }

      // Only include content when creating a new song
      if (!isEdit) {
        dataToSave.content = values.content
      }

      const response = await axios({
        url,
        method,
        data: dataToSave
      })
      setValues(response.data)

      // Update versions state if response includes additionalVersions
      if (response.data.additionalVersions !== undefined) {
        const allVersions = [
          {
            versionId: 'original',
            label: 'Original',
            content: response.data.content,
            isOriginal: true,
            createdBy: response.data.creator
          },
          ...response.data.additionalVersions
        ]
        setVersions(allVersions)
      }

      navigate(`/songs/${response.data._id}`)
    } catch (err) {
      if (err.response.status === 400) {
        setErrors(err.response.data)
      } else {
        console.log('err', err.response)
      }
    }
  }, [isEdit, activeVersionId, navigate, setValues, setErrors])

  const loadSong = useCallback(async () => {
    pageView(PAGES.SONG_EDIT)
    if (songid) {
      try {
        const response = await axios.get(`/api/songs/${songid}`)
        setValues(response.data)

        // Construct versions array: Original + additional versions
        const allVersions = [
          {
            versionId: 'original',
            label: 'Original',
            content: response.data.content,
            isOriginal: true,
            createdBy: response.data.creator
          },
          ...(response.data.additionalVersions || [])
        ]

        setVersions(allVersions)
        setActiveVersionId('original')
        setFieldValue('content', response.data.content)
      } catch (err) {
        console.log('err', err.response)
      }
    }
  }, [songid, setFieldValue])

  useEffect(() => {
    // on mount
    document.addEventListener('contextmenu', _handleContextMenu)
    document.addEventListener('click', _handleClick)
    document.addEventListener('scroll', _handleScroll)

    loadSong()
    // on unmount
    return () => {
      document.removeEventListener('contextmenu', _handleContextMenu)
      document.removeEventListener('click', _handleClick)
      document.removeEventListener('scroll', _handleScroll)
    }
  }, ['-'])

  const _handleClick = useCallback((e) => {
    if (!_find(e.path, pathPart => pathPart.id === 'songEditContext')) {
      setContextEvent(undefined)
    }
  })

  const insertChord = useCallback((inChord) => {
    const chord = `{${inChord}} `
    const element = document.getElementById('songEditContent')

    if (element.selectionStart || element.selectionStart === '0') {
      const startPos = element.selectionStart
      element.value = `${element.value.substring(0, startPos)}${chord}${element.value.substring(startPos, element.value.length)}`
    } else {
      element.value += chord
    }

    setContextEvent(undefined)
    setFieldValue('content', element.value)
  })

  const shortcutChord = useCallback((e) => {
    const element = document.getElementById('songEditContent')
    // BracketLeft and BracketRight keycodes
    if (e.shiftKey === false || (e.keyCode !== 221 && e.keyCode !== 219)) {
      return
    }

    const start = element.selectionStart
    const end = element.selectionEnd

    if (start !== undefined && end !== undefined && end - start > 0) {
      e.preventDefault()

      const before = element.value.substring(0, start)
      const content = element.value.substring(start, end)
      const after = element.value.substring(end)

      element.value = `${before}{${content}}${after}`

      setFieldValue('content', element.value)

      element.focus()
      element.selectionStart = end + 2
      element.selectionEnd = end + 2
    }
  })

  const _handleScroll = useCallback(() => {
    setContextEvent(undefined)
  })

  const _handleContextMenu = useCallback((e) => {
    if (e.target.name === 'content') {
      e.preventDefault()
      setContextEvent(e)
    }
  })

  const pasteMagician = useCallback((e) => {
    const pasted = e.clipboardData.getData('Text')
    if (looksLikeChordsAbovePaste(pasted)) {
      confirm('Looks like you\'ve pasted content with chords over the lyrics.\n\r\n\rAttempt to merge and format them?').then(
        (result) => {
          setFieldValue('content', formatFromPaste(pasted))
        }
      )
    }
  })

  // Transpose all chords in the content
  const transposeContent = useCallback((steps) => {
    const chordPattern = /\{([^}]+)\}/g
    const transposedContent = values.content.replace(chordPattern, (match, chord) => {
      const transposedChord = transposeChord(chord, steps)
      return `{${transposedChord}}`
    })
    setFieldValue('content', transposedContent)
  }, [values.content, setFieldValue])

  const handleKeyUp = useCallback(() => {
    transposeContent(1)
  }, [transposeContent])

  const handleKeyDown = useCallback(() => {
    transposeContent(-1)
  }, [transposeContent])

  // Handle version selection
  const handleVersionSelect = useCallback((versionId) => {
    const version = versions.find(v => v.versionId === versionId)
    if (version) {
      setActiveVersionId(versionId)
      setFieldValue('content', version.content)
    }
  }, [versions, setFieldValue])

  // Handle version creation
  const handleVersionCreate = useCallback(async (label) => {
    try {
      const response = await axios.post(`/api/songs/${songid}/versions`, {
        label,
        sourceVersionId: activeVersionId,
        content: values.content
      })

      const newVersion = response.data.version
      const updatedVersions = [...versions, newVersion]
      setVersions(updatedVersions)
      setActiveVersionId(newVersion.versionId)
      setFieldValue('content', newVersion.content)
    } catch (err) {
      throw new Error(err.response?.data?.msg || 'Failed to create version')
    }
  }, [songid, activeVersionId, values.content, versions, setFieldValue])

  // Handle version deletion
  const handleVersionDelete = useCallback(async (versionId, label) => {
    try {
      await confirm(`Are you sure you want to delete the "${label}" version?`)
      await axios.delete(`/api/songs/${songid}/versions/${versionId}`)

      const updatedVersions = versions.filter(v => v.versionId !== versionId)
      setVersions(updatedVersions)

      // Switch to original version if deleted version was active
      if (versionId === activeVersionId) {
        const originalVersion = updatedVersions.find(v => v.isOriginal)
        setActiveVersionId(originalVersion.versionId)
        setFieldValue('content', originalVersion.content)
      }
    } catch (err) {
      if (err.message !== 'User cancelled') {
        throw new Error(err.response?.data?.msg || 'Failed to delete version')
      }
    }
  }, [songid, versions, activeVersionId, setFieldValue])

  return (
    <div className='SongEdit'>
      <ContextMenu songContent={values.content} contextEvent={contextEvent} insertFn={insertChord} />
      <InputGroup>
        <label htmlFor='title'>Title: </label>
        <input
          type='text'
          name='title'
          value={values.title}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {touched.title && errors.title && <FormError>{errors.title}</FormError>}
        <label htmlFor='artist'>Artist: </label>
        <input
          type='text'
          name='artist'
          value={values.artist}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {touched.artist && errors.artist && <FormError>{errors.artist}</FormError>}
        <AutoChord content={values.content} updateFn={newContent => setFieldValue('content', newContent)} />
        <EditMonospace editInMonospace={editInMonospace} updateFn={change => setEditInMonospace(change)} />
        {values.transposed !== 0 && <FormInfo>Song is transposed. You are editing in the original key.</FormInfo>}
        <DetectKey songContent={values.content} />

        {/* Version Manager - only show when editing existing song */}
        {isEdit && versions.length > 0 && (
          <VersionManager
            versions={versions}
            activeVersionId={activeVersionId}
            onVersionSelect={handleVersionSelect}
            onVersionCreate={handleVersionCreate}
            onVersionDelete={handleVersionDelete}
            canEdit={isEdit}
          />
        )}

        <TransposeButtonGroup>
          <span>Transpose Content:</span>
          <SmallButton type='button' onClick={handleKeyDown} title='Transpose down one semitone'>
            Key ↓
          </SmallButton>
          <SmallButton type='button' onClick={handleKeyUp} title='Transpose up one semitone'>
            Key ↑
          </SmallButton>
        </TransposeButtonGroup>

        <ScrollSync>
          <PreviewContainer>
            <PreviewLeft>
              <label htmlFor='content'>content: </label>
              <ScrollSyncPane>
                <div>
                  <TextArea
                    monospace={editInMonospace}
                    id='songEditContent'
                    type='text'
                    name='content'
                    value={values.content}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    readOnly={contextEvent ? 'readonly' : ''}
                    onKeyDown={shortcutChord}
                    onPaste={pasteMagician}
                  />
                  {touched.content && errors.content && <FormError>{errors.content}</FormError>}
                </div>
              </ScrollSyncPane>
              <br />
            </PreviewLeft>
            <PreviewRight>
              <label>preview: </label>
              <ScrollSyncPane>
                <div>
                  <SongContent editMode content={values.content} />
                </div>
              </ScrollSyncPane>
            </PreviewRight>
          </PreviewContainer>
        </ScrollSync>
        <div style={{ clear: 'both' }} />
        <label htmlFor='media'>Media Link: {values.media === '' && <a href={getYoutubeSearchUrl(values.artist, values.title)} target='_blank' rel='noopener noreferrer'>Search</a>}</label>
        <input
          type='text'
          name='media'
          value={values.media}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {touched.media && errors.media && <FormError>{errors.media}</FormError>}
      </InputGroup>
      <div style={{ clear: 'both' }} />
      <SmallInputGroup>
        <div className='small'>
          <label htmlFor='meta1'>Meta 1: </label>
          <input
            type='text'
            name='meta1'
            value={values.meta1}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {touched.meta1 && errors.meta1 && <FormError>{errors.meta1}</FormError>}
        </div>
        <div className='small'>
          <label htmlFor='meta2'>Meta 2: </label>
          <input
            type='text'
            name='meta2'
            value={values.meta2}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {touched.meta2 && errors.meta2 && <FormError>{errors.meta2}</FormError>}
        </div>
        <div className='small'>
          <label htmlFor='meta3'>Meta 3: </label>
          <input
            type='text'
            name='meta3'
            value={values.meta3}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {touched.meta3 && errors.meta3 && <FormError>{errors.meta3}</FormError>}
        </div>
        <div className='small'>
          <label htmlFor='meta4'>Meta 4: </label>
          <input
            type='text'
            name='meta4'
            value={values.meta4}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {touched.meta4 && errors.meta4 && <FormError>{errors.meta4}</FormError>}
        </div>
      </SmallInputGroup>
      <div style={{ clear: 'both' }} />
      <SmallInputGroup>
        <div className='small'>
          <label htmlFor='key'>Key: </label>
          <Select
            value={values.key ? { value: values.key, label: values.key } : null}
            onChange={(option) => {
              const syntheticEvent = { target: { name: 'key', value: option ? option.value : '' } }
              handleChange(syntheticEvent)
            }}
            options={SONG_KEYS.map(songKey => ({ value: songKey, label: songKey }))}
            isClearable
          />
        </div>
        <div className='small'>
          <label htmlFor='tempo'>Tempo: </label>
          <input
            type='text'
            name='tempo'
            value={values.tempo}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {touched.tempo && errors.tempo && <FormError>{errors.tempo}</FormError>}
        </div>
        <div className='small'>
          <label htmlFor='signature'>Signature: </label>
          <Select
            value={values.signature ? SIGNATURES.find(sig => sig.value === values.signature) ? { value: values.signature, label: SIGNATURES.find(sig => sig.value === values.signature).display } : null : null}
            onChange={(option) => {
              const syntheticEvent = { target: { name: 'signature', value: option ? option.value : '' } }
              handleChange(syntheticEvent)
            }}
            options={SIGNATURES.map(sig => ({ value: sig.value, label: sig.display }))}
            isClearable
          />
        </div>
        <div className='small'>
          <label htmlFor='duration'>Duration: (mm:ss)</label>
          <input
            type='text'
            name='duration'
            value={values.duration}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {touched.duration && errors.duration && <FormError>{errors.duration}</FormError>}
        </div>
      </SmallInputGroup>
      <PrimaryButton type='submit' onClick={handleSubmit}>Save Song</PrimaryButton>
    </div>
  )
}

export default EditSong
